import React, { useEffect, useState, useMemo } from 'react';

import { Box, ClickAwayListener } from '@mui/material';
import { Table, createColumnHelper } from '@bestdoctor/spectrum-uikit/Table';
import type { ColumnDef } from '@tanstack/react-table';
import DescriptionIcon from '@mui/icons-material/Description';
import MenuIcon from '@mui/icons-material/Menu';
import LinkIcon from '@mui/icons-material/Link';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton } from '@mui/material';

import { TooltipActionBtns } from 'shared/tooltip/TooltipActionBtns/TooltipActionBtns';
import { ActionDocModal } from 'shared/modals/ActionDocModal/ActionDocModal';
import { ChartPaymentsDocsModal } from 'shared/modals/ChartPaymentsDocsModal/ChartPaymentsDocsModal';

import { throttle } from 'utils/tablesMethods';

import {
  tableDocsCellStyle,
  tableDocsStatusCellStyle,
  tableDocsIconStyle,
  tableCellDocsStatusStyle,
} from 'ui/tables/tables';

import { IListAddAgreementAndPayments } from 'interfaces/documents/documentsInterfaces';
import { DocumetsChangeHistory } from 'shared/modals/DocumetsChangeHistory/DocumetsChangeHistory';
import { TooltipAppendixDocs } from 'shared/tooltip/TooltipAppendixDocs/TooltipAppendixDocs';
import { DocumentViewModal } from 'shared/modals/DocumentViewModal/DocumentViewModal';
import { ApproveDocumentModal } from 'shared/modals/ApproveDocumentModal/ApproveDocumentModal';
import { SendDocumentsModal } from 'shared/modals/SendDocumentsModal/SendDocumentsModal';
import { CancelConfirmModal } from 'shared/modals/ActionDocModal/CancelConfirmModal';
import { Loader } from 'shared/loaders/Loader';
import { agreementStatus } from 'utils/agreementStatus';
import { downloadDoc } from 'utils/downloadDoc';
import { TooltipActionsInfo } from 'ui/tooltips/tooltipAction';
import { AccessList, DefaultPermissions } from 'interfaces/global/globalInterfaces';

interface IListAddAgreementAndPaymentsProps {
  handleOnScroll: (value: boolean) => void;
  onScrollOpen: boolean;
  supplementaryAgreements: IListAddAgreementAndPayments[];
  fetchNextPage: () => void;
  isFetchingNextPageSupplementaryAgreements: boolean;
  hasNextPageSupplementaryAgreements: boolean;
  handleOrderingAgreement: (value: string) => void;
  orderingAgreement: string;
  accessList: AccessList[];
  defaultPermissions: DefaultPermissions;
  refetchSupplementaryAgreements: () => void;
}

const columnHelper = createColumnHelper<IListAddAgreementAndPayments>();

export const AddAgreementAndPayments: React.FC<IListAddAgreementAndPaymentsProps> = ({
  supplementaryAgreements,
  fetchNextPage,
  isFetchingNextPageSupplementaryAgreements,
  hasNextPageSupplementaryAgreements,
  handleOrderingAgreement,
  orderingAgreement,
  onScrollOpen,
  handleOnScroll,
  accessList,
  defaultPermissions,
  refetchSupplementaryAgreements,
}) => {
  // стейты для модалок
  const [isActionDocModal, setActionDocModal] = useState<boolean>(false);
  const [isChartPaymentsDocsModal, setChartPaymentsDocsModal] = useState<boolean>(false);
  const [isAppendixList, setAppendixList] = useState<boolean>(false);
  const [isDocumentViewModal, setDocumentViewModal] = useState<boolean>(false);
  const [isApproveDocumentModal, setApproveDocumentModal] = useState<boolean>(false);
  const [isSendDocumentModal, setSendDocumentModal] = useState<boolean>(false);
  const [isCancelConfirmModal, setCancelConfirmModal] = useState<boolean>(false);
  const [documentModalTitle, setModalTitle] = useState<string>('Предпросмотр файла');
  const [documentModalLink, setDocumentModalLink] = useState<string>('');
  // ChartPaymentsDocsModal
  const [isBottom, setIsBottom] = useState<boolean>(false);
  // стейты кнопки действия
  const [isTooltipActions, setTooltipActions] = useState<boolean>(false);
  const [tooltipSelected, setTooltipSelected] = useState<IListAddAgreementAndPayments | null>(null);

  const agreements: IListAddAgreementAndPayments[] = supplementaryAgreements || [];

  const [isDocumentsChangeHistory, setDocumentsChangeHistory] = useState<boolean>(false);

  // Сортировка
  const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>([]);

  useEffect(() => {
    if (sorting.length > 0) {
      const sort = sorting[0];
      const sortString = sort.desc ? `-${sort.id}` : sort.id;
      handleOrderingAgreement(sortString);
    } else {
      handleOrderingAgreement('');
    }
  }, [sorting, handleOrderingAgreement]);

  // Колонки таблицы
  const columns = useMemo<ColumnDef<IListAddAgreementAndPayments, any>[]>(
    () => [
      columnHelper.accessor('number', {
        header: 'Наименование документа',
        cell: (info) => (
          <Box sx={tableDocsCellStyle} component="span">
            {info.getValue()}
          </Box>
        ),
        enableSorting: true,
      }) as ColumnDef<IListAddAgreementAndPayments, any>,
      columnHelper.accessor('supplementary_agreement_file', {
        header: 'Документ',
        cell: (info) => {
          const row = info.row.original;
          return (
            <Box
              sx={{
                ...tableDocsCellStyle,
                display: 'flex',
                alignItems: 'center',
                '&:hover': { cursor: 'pointer' },
              }}
              onClick={() => {
                downloadDoc(row.supplementary_agreement_file);
              }}
            >
              <DescriptionIcon sx={tableDocsIconStyle} />
              Документ
            </Box>
          );
        },
      }) as ColumnDef<IListAddAgreementAndPayments, any>,
      columnHelper.accessor('appendix', {
        header: 'Приложения',
        cell: (info) => {
          const row = info.row.original;
          return (
            <Box sx={tableDocsCellStyle}>
              <TooltipAppendixDocs
                onScrollOpen={onScrollOpen}
                data={tooltipSelected}
                uuid={`appendix-key-${row.uuid}`}
                open={isAppendixList}
                handleClose={() => setAppendixList(false)}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover': { cursor: 'pointer' },
                  }}
                  onClick={() => {
                    setTooltipSelected(row);
                    setAppendixList(true);
                  }}
                >
                  <MenuIcon sx={tableDocsIconStyle} />
                  Приложения
                </Box>
              </TooltipAppendixDocs>
            </Box>
          );
        },
      }) as ColumnDef<IListAddAgreementAndPayments, any>,
      columnHelper.accessor('original_bill_file_url', {
        header: 'Счёт',
        cell: (info) => {
          const row = info.row.original;
          return (
            <TooltipActionsInfo
              title={row.original_bill_file_url ? '' : 'Файл счёта отсутствует'}
              placement="left"
            >
              <Box
                sx={{
                  ...tableDocsCellStyle,
                  display: 'flex',
                  alignItems: 'center',
                  opacity: row.original_bill_file_url ? 1 : 0.5,
                  pointerEvents: row.original_bill_file_url ? 'auto' : 'none',
                  '&:hover': { cursor: row.original_bill_file_url ? 'pointer' : 'none' },
                }}
                onClick={() => {
                  if (row.original_bill_file_url) {
                    setTooltipSelected(row);
                    window.open(row.original_bill_file_url);
                  }
                }}
              >
                <LinkIcon
                  sx={{
                    height: '14px',
                  }}
                />
                Ссылка на счёт
              </Box>
            </TooltipActionsInfo>
          );
        },
      }) as ColumnDef<IListAddAgreementAndPayments, any>,
      columnHelper.accessor('hr_status', {
        header: 'Статус',
        cell: (info) => {
          const row = info.row.original;
          return (
            <Box sx={() => tableDocsStatusCellStyle()}>
              <Box
                sx={() => tableCellDocsStatusStyle(row)}
                onClick={() => {
                  setTooltipSelected(row);
                }}
              >
                {agreementStatus[row.hr_status]}
              </Box>
              <TooltipActionBtns
                handleDocumentViewModal={() => setDocumentViewModal(true)}
                onScrollOpen={onScrollOpen}
                handleApproveDocumentModal={() => setApproveDocumentModal(true)}
                open={isTooltipActions}
                status={tooltipSelected?.hr_status}
                isAgreements
                uuid={`tooltip-key-${row.uuid}`}
                data={tooltipSelected}
                handleDocumentsChangeHistoryModal={() => setDocumentsChangeHistory(true)}
                handleActionDoc={() => setActionDocModal(true)}
                handleTooltipClose={() => setTooltipActions(false)}
                handleDocumentModalLink={setDocumentModalLink}
                handleModalTitle={setModalTitle}
                accessList={accessList}
                defaultPermissions={defaultPermissions}
              >
                <IconButton
                  sx={{
                    color: '#FF6D6D',
                  }}
                  aria-label="actions"
                  onClick={(evt) => {
                    evt.stopPropagation();
                    setTooltipSelected(row);
                    setTooltipActions(true);
                    setAppendixList(false);
                  }}
                >
                  <MoreVertIcon fontSize="small" sx={{ color: '#FF6D6D' }} />
                </IconButton>
              </TooltipActionBtns>
            </Box>
          );
        },
        enableSorting: false,
      }) as ColumnDef<IListAddAgreementAndPayments, any>,
    ],
    [
      onScrollOpen,
      isAppendixList,
      tooltipSelected,
      isTooltipActions,
      accessList,
      defaultPermissions,
    ]
  );

  const handleScroll = (evt: Event): void => {
    const { scrollHeight, scrollTop } = (evt.target as Document).documentElement;
    const windowHeight = window.innerHeight;

    if (scrollHeight - (windowHeight + scrollTop) < 50) {
      setIsBottom(true);
    }
  };

  useEffect(() => {
    if (hasNextPageSupplementaryAgreements && !isFetchingNextPageSupplementaryAgreements) {
      document.addEventListener('scroll', throttle(handleScroll, 1000));
    }

    return () => document.removeEventListener('scroll', throttle(handleScroll, 1000));
  }, [hasNextPageSupplementaryAgreements, isFetchingNextPageSupplementaryAgreements]);

  useEffect(() => {
    if (isBottom) {
      fetchNextPage();
      setIsBottom(false);
    }
  }, [fetchNextPage, isBottom]);

  useEffect(() => {
    if (!onScrollOpen && (isAppendixList || isTooltipActions)) {
      handleOnScroll(true);
    }
  }, [handleOnScroll, onScrollOpen, isAppendixList, tooltipSelected, isTooltipActions]);

  // Преобразуем sorting для Table
  const tableSorting = useMemo(() => {
    if (sorting.length === 0) return undefined;
    return {
      state: sorting,
      setState: setSorting,
    };
  }, [sorting]);

  // Инициализируем sorting из orderingAgreement
  useEffect(() => {
    if (orderingAgreement) {
      const isDesc = orderingAgreement.startsWith('-');
      const field = isDesc ? orderingAgreement.slice(1) : orderingAgreement;
      if (field === 'number') {
        setSorting([{ id: field, desc: isDesc }]);
      }
    } else {
      setSorting([]);
    }
  }, [orderingAgreement]);

  return (
    <ClickAwayListener onClickAway={() => setAppendixList(false)}>
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '20px',
            width: '100%',
          }}
        >
          <Table
            data={agreements}
            columns={columns}
            sorting={tableSorting}
            enableSorting
            manualSorting
          />
          {isFetchingNextPageSupplementaryAgreements && <Loader />}
        </Box>

        <DocumentViewModal
          title={documentModalTitle}
          link={documentModalLink}
          open={isDocumentViewModal}
          handleClose={() => {
            setDocumentViewModal(false);
          }}
        />

        <ActionDocModal
          open={isActionDocModal}
          data={tooltipSelected}
          handleClose={() => setActionDocModal(false)}
          handleCancelConfirmModal={() => setCancelConfirmModal(true)}
          refetchSupplementaryAgreements={refetchSupplementaryAgreements}
        />

        <ChartPaymentsDocsModal open={isChartPaymentsDocsModal} handleClose={() => setChartPaymentsDocsModal(false)} />

        <DocumetsChangeHistory
          data={tooltipSelected}
          open={isDocumentsChangeHistory}
          handleClose={() => setDocumentsChangeHistory(false)}
        />

        <ApproveDocumentModal
          data={tooltipSelected}
          open={isApproveDocumentModal}
          handleClose={() => setApproveDocumentModal(false)}
          handleSetDocumentModal={() => setSendDocumentModal(true)}
          refetchSupplementaryAgreements={refetchSupplementaryAgreements}
        />

        <SendDocumentsModal
          data={tooltipSelected}
          open={isSendDocumentModal}
          handleClose={() => setSendDocumentModal(false)}
        />

        <CancelConfirmModal open={isCancelConfirmModal} handleClose={() => setCancelConfirmModal(false)} />
      </Box>
    </ClickAwayListener>
  );
};
